import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {forwardRef, useImperativeHandle} from 'react';

import {isArray} from 'ramda-adjunct';

import {useDeleteReceiveNoteCorrectionMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  refreshDmsDatagrid,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export type ReceiveNoteCorrectionsHandle = {
  refreshDataGrid: () => void;
};

export const ReceiveNoteCorrectionsList = forwardRef<ReceiveNoteCorrectionsHandle>((_, ref) => {
  const navigate = useNavigate();
  const [deleteReceiveNoteCorrection] = useDeleteReceiveNoteCorrectionMutation();

  useImperativeHandle(
    ref,
    () => ({
      refreshDataGrid: () => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNote),
    }),
    []
  );

  const handleDelete = (rowId: string | string[], deselectAll: () => void) => {
    openDeleteDialog({
      onConfirm: async () =>
        await deleteReceiveNoteCorrection({body: {creditNoteId: isArray(rowId) ? rowId : [rowId]}})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(deselectAll)
          .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNote))
          .catch(handleApiError),
      'data-testid': testIds.warehouse.receiveNoteCorrectionsList('deleteDialog'),
    });
  };

  const actionCallback: ActionCallback = ({actionKey, rowId, deselectAll, sourceSearchParams}) => {
    match(actionKey)
      .with('redirectDetail', () => {
        const id = isArray(rowId) ? rowId[0] : rowId;
        navigate(
          composePath(warehouseRoutes.receiveNoteCorrectionDetailOverview, {
            queryParams: sourceSearchParams,
            params: {id},
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('delete', () => {
        handleDelete(rowId, deselectAll);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper hideCardWrapper>
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.warehouseCreditNote}
        actionCallback={actionCallback}
        data-testid={WAREHOUSE_GRID_CODES.warehouseCreditNote}
      />
    </FullscreenDataGridWrapper>
  );
});
